import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../lib/api.ts';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { Field, Select } from '../components/Field.tsx';
import type { Company, Json, Questionnaire } from '../lib/types.ts';

type StatusCounts = Record<string, number> & { total: number };
type Summary = {
  companies: StatusCounts;
  drivers: StatusCounts;
  submissions: StatusCounts;
  asset_audits: StatusCounts;
  corrective_actions: StatusCounts;
  verifications: StatusCounts;
  training: { completions: number };
  visitors_on_site: number;
};

type ResponseRow = {
  id: string;
  answer: Json;
  comment: string | null;
  status: string;
  created_at: string;
  question: { label: string; type: string; questionnaire_id: string | null } | null;
  company: { company_name: string } | null;
  driver: { name: string } | null;
};

function fmtAnswer(a: Json): string {
  if (a === null || a === undefined) return '—';
  if (typeof a === 'boolean') return a ? 'Yes' : 'No';
  if (Array.isArray(a)) return a.join(', ');
  if (typeof a === 'object') return JSON.stringify(a);
  return String(a);
}

export function AdminReports() {
  const summaryQuery = useQuery({
    queryKey: ['reports', 'summary'],
    queryFn: () => adminApi.get<Summary>('/api/reports/summary'),
  });
  const companiesQuery = useQuery({
    queryKey: ['companies', 'forReports'],
    queryFn: () => adminApi.get<{ companies: Company[] }>('/api/companies'),
  });
  const questionnairesQuery = useQuery({
    queryKey: ['questionnaires'],
    queryFn: () => adminApi.get<{ questionnaires: Questionnaire[] }>('/api/questionnaires'),
  });

  const [companyId, setCompanyId] = useState('');
  const [questionnaireId, setQuestionnaireId] = useState('');
  const [status, setStatus] = useState('');

  const responsesQuery = useQuery({
    queryKey: ['reports', 'responses', companyId, questionnaireId, status],
    queryFn: () =>
      adminApi.get<{ responses: ResponseRow[] }>('/api/reports/responses', {
        query: {
          company_id: companyId || undefined,
          questionnaire_id: questionnaireId || undefined,
          status: status || undefined,
          limit: 100,
        },
      }),
  });

  const s = summaryQuery.data;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Reports</h1>
        <p className="mt-1 text-sm text-slate-500">
          Compliance, training, asset audits and outstanding actions — with drill-down into
          individual question responses.
        </p>
      </div>

      {summaryQuery.isLoading || !s ? (
        <CenteredSpinner />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Card title="Companies" total={s.companies.total}>
            <Line label="Compliant" value={s.companies.compliant} tone="good" />
            <Line label="In progress" value={s.companies.in_progress} />
            <Line label="Changes / review" value={(s.companies.changes ?? 0) + (s.companies.review ?? 0)} />
            <Line label="Expired" value={s.companies.expired} tone="bad" />
          </Card>
          <Card title="Drivers" total={s.drivers.total}>
            <Line label="Compliant" value={s.drivers.compliant} tone="good" />
            <Line label="In progress" value={s.drivers.in_progress} />
          </Card>
          <Card title="Question responses" total={s.submissions.total}>
            <Line label="Approved" value={s.submissions.approved} tone="good" />
            <Line label="Pending" value={s.submissions.pending} />
            <Line label="Not compliant" value={s.submissions.rejected} tone="bad" />
          </Card>
          <Card title="Document verification" total={s.verifications.total}>
            <Line label="Auto-passed" value={s.verifications.auto_passed} tone="good" />
            <Line label="Needs review" value={s.verifications.needs_review} tone="warn" />
            <Line label="Rejected" value={s.verifications.manual_failed} tone="bad" />
          </Card>
          <Card title="Asset audits" total={s.asset_audits.total}>
            <Line label="Compliant" value={s.asset_audits.compliant} tone="good" />
            <Line label="Not compliant" value={s.asset_audits.not_compliant} tone="bad" />
            <Line label="In progress" value={s.asset_audits.in_progress} />
          </Card>
          <Card title="Corrective actions" total={s.corrective_actions.total}>
            <Line label="Open" value={s.corrective_actions.open} tone="warn" />
            <Line label="To review" value={s.corrective_actions.responded} />
            <Line label="Approved" value={s.corrective_actions.approved} tone="good" />
          </Card>
          <Card title="Training" total={s.training.completions}>
            <Line label="Completions" value={s.training.completions} tone="good" />
          </Card>
          <Card title="Visitors on site" total={s.visitors_on_site}>
            <Line label="On site now" value={s.visitors_on_site} />
          </Card>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="mb-3 text-sm font-semibold text-slate-900">Question responses</h2>
        <div className="mb-4 grid gap-3 sm:grid-cols-3">
          <Field label="Company">
            <Select value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
              <option value="">All companies</option>
              {(companiesQuery.data?.companies ?? []).map((co) => (
                <option key={co.id} value={co.id}>{co.company_name}</option>
              ))}
            </Select>
          </Field>
          <Field label="Questionnaire">
            <Select value={questionnaireId} onChange={(e) => setQuestionnaireId(e.target.value)}>
              <option value="">All questionnaires</option>
              {(questionnairesQuery.data?.questionnaires ?? []).map((qn) => (
                <option key={qn.id} value={qn.id}>{qn.name}</option>
              ))}
            </Select>
          </Field>
          <Field label="Status">
            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Any status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Not compliant</option>
              <option value="not_applicable">Not applicable</option>
            </Select>
          </Field>
        </div>

        {responsesQuery.isLoading ? (
          <CenteredSpinner />
        ) : (responsesQuery.data?.responses.length ?? 0) === 0 ? (
          <p className="text-sm italic text-slate-400">No responses match these filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100 text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2">Question</th>
                  <th className="px-3 py-2">Owner</th>
                  <th className="px-3 py-2">Answer</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(responsesQuery.data?.responses ?? []).map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 text-slate-700">{r.question?.label ?? '—'}</td>
                    <td className="px-3 py-2 text-slate-700">
                      {r.company?.company_name ?? r.driver?.name ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{fmtAnswer(r.answer)}</td>
                    <td className="px-3 py-2 text-slate-600">{r.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Card({ title, total, children }: { title: string; total: number; children: ReactNode }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-semibold text-slate-900">{title}</span>
        <span className="text-2xl font-bold text-slate-900">{total}</span>
      </div>
      <div className="mt-2 space-y-1">{children}</div>
    </div>
  );
}

function Line({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | undefined;
  tone?: 'good' | 'bad' | 'warn';
}) {
  const color =
    tone === 'good' ? 'text-emerald-700' : tone === 'bad' ? 'text-red-700' : tone === 'warn' ? 'text-amber-700' : 'text-slate-600';
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-slate-500">{label}</span>
      <span className={`font-medium ${color}`}>{value ?? 0}</span>
    </div>
  );
}
